import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { Button, InlineNotification } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { useHistory } from 'react-router-dom';
import { PageHeader } from 'components/page-header';
import { format0 } from 'utils/number';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { usePolling } from 'lib/hooks/usePolling';
import type { ClusteringRun, StartProcessResponse } from './types';
import {
  clusteringRunsAtom,
  clusteringRunsFingerprint,
  fetchClusteringRuns,
} from './atoms';
import {
  ACTIVE_BATCH_STATUSES,
  BULK_TYPE_LABELS,
  JOB_TYPE_LABELS,
  PLAIN_TABLE_CONFIG,
  ProcessProgress,
  StatusTag,
  runColumn,
} from './shared';
import { useProcessStatus } from './use-process-status';
import { NormalizeModal } from './normalize-modal';
import { GenerateModal } from './generate-modal';
import { AddressScopeModal } from './address-scope-modal';
import styles from './page.module.scss';

// Hub: one table of clustering runs (generate -> review -> execute lifecycle).
// Launch actions live in the header; review & execute live on the run's own
// page (/admin/bulk_dedupe/runs/:id), so a run survives a refresh.
export const BulkDedupePage: FC = () => {
  const history = useHistory();
  const [runsState, refetchRuns] = useAtom(clusteringRunsAtom);
  const [normalizeOpen, setNormalizeOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [normProc, setNormProc] = useState<StartProcessResponse | null>(null);
  const normStatus = useProcessStatus(normProc);

  const runs = runsState.state === 'hasData' ? runsState.data : [];
  const loading = runsState.state === 'loading';
  const error =
    runsState.state === 'hasError'
      ? runsState.error instanceof Error
        ? runsState.error.message
        : 'Failed to load clustering runs'
      : null;

  // Live-refresh while any run is still generating, but only refetch (and
  // re-render) when the payload actually changed, so the table doesn't flicker.
  const fingerprintRef = useRef('');
  useEffect(() => {
    if (runsState.state === 'hasData') {
      fingerprintRef.current = clusteringRunsFingerprint(runsState.data);
    }
  }, [runsState]);

  // Refetch on mount so returning from a run-detail execute shows fresh counts
  // — the atom otherwise serves the cached page from the last visit.
  useEffect(() => {
    refetchRuns();
  }, [refetchRuns]);

  const anyActive = runs.some((r) => ACTIVE_BATCH_STATUSES.has(r.status));
  const pollRuns = useCallback(async () => {
    const fresh = await fetchClusteringRuns();
    if (clusteringRunsFingerprint(fresh) !== fingerprintRef.current)
      refetchRuns();
  }, [refetchRuns]);

  usePolling({
    fn: pollRuns,
    intervalMs: 4000,
    enabled: anyActive,
    immediate: false,
  });

  const columns = useMemo<DataTableColumnDef<ClusteringRun>[]>(
    () => [
      runColumn<ClusteringRun>('Run'),
      {
        accessorKey: 'bulkType',
        header: 'Type',
        size: 110,
        enableSorting: false,
        cell: ({ getValue }) =>
          BULK_TYPE_LABELS[getValue() as ClusteringRun['bulkType']] ??
          BULK_TYPE_LABELS.entities,
      },
      {
        accessorKey: 'jobType',
        header: 'Job type',
        size: 170,
        enableSorting: false,
        cell: ({ getValue }) => {
          const jobType = getValue() as string;
          return JOB_TYPE_LABELS[jobType] ?? jobType;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 170,
        enableSorting: false,
        cell: ({ row }) => (
          <StatusTag
            status={row.original.status}
            bulkType={row.original.bulkType}
          />
        ),
      },
      {
        accessorKey: 'actor',
        header: 'Started by',
        size: 160,
        enableSorting: false,
        cell: ({ getValue }) => (getValue() as string | null) ?? '--',
      },
      {
        accessorKey: 'total',
        header: 'Candidates',
        size: 110,
        enableSorting: false,
        cell: ({ getValue }) => format0(getValue() as number),
      },
      {
        id: 'in_scope',
        header: 'Included',
        size: 100,
        enableSorting: false,
        // Partially excluded groups still execute, so they count as included —
        // matching the run page's Execute total.
        cell: ({ row }) =>
          format0(
            (row.original.counts.in_scope ?? 0) +
              (row.original.counts.partially_excluded ?? 0)
          ),
      },
      {
        id: 'combined',
        // One column for both run types: entities merge, addresses combine.
        header: 'Merged / Combined',
        size: 150,
        enableSorting: false,
        cell: ({ row }) => format0(row.original.counts.combined ?? 0),
      },
      {
        id: 'failed',
        header: 'Failed',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => format0(row.original.counts.failed ?? 0),
      },
      {
        id: 'reversed',
        header: 'Reversed',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => format0(row.original.counts.reversed ?? 0),
      },
    ],
    []
  );

  return (
    <div className={styles.page}>
      <PageHeader
        breadcrumbs={[]}
        title="Bulk Dedupe"
        actions={
          <div className={styles.headerActions}>
            <Button
              kind="ghost"
              size="sm"
              onClick={() => setNormalizeOpen(true)}
            >
              Normalize Addresses
            </Button>
            <Button
              kind="secondary"
              size="sm"
              renderIcon={Add}
              onClick={() => setAddressesOpen(true)}
            >
              Addresses
            </Button>
            <Button
              kind="primary"
              size="sm"
              renderIcon={Add}
              onClick={() => setGenerateOpen(true)}
            >
              Generate Candidates
            </Button>
          </div>
        }
      />

      {normProc && (
        <ProcessProgress label="Normalizing addresses" status={normStatus} />
      )}

      {error ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn’t load clustering runs"
          subtitle={error}
        />
      ) : !loading && runs.length === 0 ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="No clustering runs yet"
          subtitle="Normalize addresses, then generate candidates to start a run."
        />
      ) : (
        <DataTable<ClusteringRun>
          data={runs}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          config={PLAIN_TABLE_CONFIG}
          onSelectionChange={(s) => {
            const id = s.selection.toggledNodes[0];
            if (!id) return;
            // Carry the run type so the detail page skips its run-type probe.
            const picked = runs.find((r) => String(r.id) === id);
            history.push({
              pathname: `/admin/bulk_dedupe/runs/${id}`,
              state: picked ? { bulkType: picked.bulkType } : undefined,
            });
          }}
        />
      )}

      <NormalizeModal
        open={normalizeOpen}
        onClose={() => setNormalizeOpen(false)}
        onStarted={setNormProc}
      />
      <GenerateModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onStarted={(proc) =>
          history.push({
            pathname: `/admin/bulk_dedupe/runs/${proc.processId}`,
            state: proc.bulkType ? { bulkType: proc.bulkType } : undefined,
          })
        }
      />
      <AddressScopeModal
        open={addressesOpen}
        onClose={() => setAddressesOpen(false)}
        onStarted={(proc) =>
          // The cluster endpoint returns the run's type; carry it so the run
          // page skips its redundant type probe.
          history.push({
            pathname: `/admin/bulk_dedupe/runs/${proc.processId}`,
            state: proc.bulkType ? { bulkType: proc.bulkType } : undefined,
          })
        }
      />
    </div>
  );
};
